/**
 * Campagnes du Live School — accès admin (PostgREST direct + RLS admin, pas
 * d'edge fn). Les tables `live_*` sont postérieures aux types stale → client
 * générique (cf. CLAUDE.md). Validation des entrées côté composant (Zod léger).
 *
 * Désactiver / supprimer une campagne (ou retirer un slot) ANNULE ses défis
 * encore `scheduled` à venir, pour que la bascule prenne effet immédiatement (le
 * seed ne les recrée qu'au prochain tick si la campagne redevient active).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { SchoolLevel, SubjectSlug } from '@jeraisonne/db';
import type { FrequencyUnit } from '@jeraisonne/db/live';
import { supabase } from '@/lib/supabase';
import {
  planCampaignReconcile,
  planCampaignParamSync,
  planCampaignTimeShift,
  applyLocalTimeOfDay,
  type ExistingChallenge,
  type ParamSyncChallenge,
  type ReconcileCampaign,
  type ReconcileSlot,
  type TimeShiftChallenge,
} from '@/lib/live-campaign-reconcile';

const db = supabase as unknown as SupabaseClient;

// Horizon de matérialisation des défis (jours) — miroir du défaut serveur
// (`LIVE_SEED_HORIZON_DAYS` dans `live-campaign-seed`).
const SEED_HORIZON_DAYS = 8;

export interface LiveCampaignSlot {
  id: string;
  campaign_id: string;
  level: SchoolLevel;
  subject: SubjectSlug | null;
  frequency_count: number;
  frequency_unit: FrequencyUnit;
  step_count: number | null;
  step_duration_ms: number | null;
}

export interface LiveCampaign {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  starts_at: string;
  ends_at: string | null;
  is_active: boolean;
  step_duration_ms: number;
  step_count: number;
  tolerance_ms: number;
  illustration_url: string | null;
  illustration_prompt: string | null;
  created_at: string;
  /** 'slots' (créneaux), 'sunday' (Live School du dimanche), 'special' (vacances, 0332). */
  kind: LiveCampaignKind;
  /** Spécial : jour de la grande journée (AAAA-MM-JJ) et heure du 1er live (HH:MM:SS). */
  special_date: string | null;
  special_time: string;
  prize_top_n: number | null;
  live_campaign_slots: LiveCampaignSlot[];
}

export type LiveCampaignKind = 'slots' | 'sunday' | 'special';

/**
 * Type effectif d'une campagne. Le Live School du dimanche (0329) est reconnu à
 * son slug : ses lives sont générés en SQL, jamais par les créneaux.
 */
export function campaignKind(c: { slug: string; kind?: string | null }): LiveCampaignKind {
  if (c.slug === 'live-du-dimanche') return 'sunday';
  return c.kind === 'special' || c.kind === 'sunday' ? c.kind : 'slots';
}

export interface CampaignInput {
  id?: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  starts_at: string; // ISO UTC
  ends_at: string | null;
  is_active: boolean;
  /** Durée d'une question en ms (défaut 60 000 = 1 min). Éditable par l'admin. */
  step_duration_ms: number;
  /** Création d'un spécial des vacances (sinon campagne à créneaux). */
  kind?: LiveCampaignKind;
  special_date?: string | null;
  special_time?: string;
  prize_top_n?: number | null;
}

export interface SlotInput {
  id?: string;
  campaign_id: string;
  level: SchoolLevel;
  subject: SubjectSlug | null;
  frequency_count: number;
  frequency_unit: FrequencyUnit;
}

const CAMPAIGNS_KEY = ['live-campaigns'] as const;

export function slugify(input: string): string {
  return input
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 80);
}

export function useLiveCampaigns() {
  return useQuery({
    queryKey: CAMPAIGNS_KEY,
    queryFn: async (): Promise<LiveCampaign[]> => {
      const { data, error } = await db
        .from('live_campaigns')
        .select(
          'id, slug, title, subtitle, description, starts_at, ends_at, is_active, step_duration_ms, step_count, tolerance_ms, illustration_url, illustration_prompt, created_at, kind, special_date, special_time, prize_top_n, live_campaign_slots(id, campaign_id, level, subject, frequency_count, frequency_unit, step_count, step_duration_ms)',
        )
        .order('created_at', { ascending: false });
      if (error) throw error;
      return ((data as LiveCampaign[] | null) ?? []).map((c) => ({
        ...c,
        live_campaign_slots: c.live_campaign_slots ?? [],
      }));
    },
  });
}

/** Annule les défis encore programmés (à venir) d'une campagne ou d'un slot. */
async function cancelFutureScheduled(filter: {
  campaign_id?: string;
  campaign_slot_id?: string;
}): Promise<void> {
  let q = db
    .from('live_challenges')
    .delete()
    .eq('status', 'scheduled')
    .gt('starts_at', new Date().toISOString());
  if (filter.campaign_id) q = q.eq('campaign_id', filter.campaign_id);
  if (filter.campaign_slot_id) q = q.eq('campaign_slot_id', filter.campaign_slot_id);
  await q;
}

/**
 * Réconcilie les défis PROGRAMMÉS (status `scheduled`, à venir) d'une campagne
 * avec sa planification courante (dates campagne + slots/cadences). À appeler
 * après toute modification de campagne/slot pour que la bascule prenne effet
 * immédiatement, sans attendre le prochain tick du cron `live-campaign-seed`
 * (qui ne fait qu'ajouter, jamais supprimer).
 *
 * - Campagne inactive/terminée → annule simplement les défis à venir.
 * - Sinon → diff `planCampaignReconcile` : supprime les obsolètes, insère les
 *   nouveaux, conserve les inchangés (exercice épinglé + payload préservés).
 *
 * No-op quand rien de structurant n'a changé (ex. édition du titre seul).
 */
async function reconcileCampaign(campaignId: string): Promise<void> {
  const now = Date.now();
  const horizonMs = SEED_HORIZON_DAYS * 86_400_000;
  const nowIso = new Date(now).toISOString();

  const { data: campaignData } = await db
    .from('live_campaigns')
    .select(
      'id, slug, kind, starts_at, ends_at, is_active, step_count, step_duration_ms, tolerance_ms',
    )
    .eq('id', campaignId)
    .maybeSingle();
  if (!campaignData) return;
  const campaign = campaignData as ReconcileCampaign & {
    is_active: boolean;
    slug: string;
    kind: string;
  };
  // Dimanche / spécial : lives générés en SQL (pas de créneaux). Les réconcilier
  // avec des créneaux vides les supprimerait tous → on ne touche à rien.
  if (campaignKind(campaign) !== 'slots') return;

  // Inactive ou terminée : on n'entretient aucune occurrence à venir.
  const ended = campaign.ends_at != null && Date.parse(campaign.ends_at) < now;
  if (!campaign.is_active || ended) {
    await cancelFutureScheduled({ campaign_id: campaignId });
    return;
  }

  const { data: slotsData } = await db
    .from('live_campaign_slots')
    .select('id, level, subject, frequency_count, frequency_unit, step_count, step_duration_ms')
    .eq('campaign_id', campaignId);
  const slots = (slotsData as ReconcileSlot[] | null) ?? [];

  const { data: existingData } = await db
    .from('live_challenges')
    .select(
      'id, level, subject, starts_at, campaign_slot_id, step_count, step_duration_ms, tolerance_ms',
    )
    .eq('campaign_id', campaignId)
    .eq('status', 'scheduled')
    .gt('starts_at', nowIso);
  const existing = (existingData as (ExistingChallenge & ParamSyncChallenge)[] | null) ?? [];

  const plan = planCampaignReconcile(campaign, slots, existing, { now, horizonMs });

  if (plan.toDelete.length) {
    await db.from('live_challenges').delete().in('id', plan.toDelete);
  }
  if (plan.toInsert.length) {
    // Idempotent : un créneau déjà pris (par une autre campagne) est ignoré.
    await db
      .from('live_challenges')
      .upsert(plan.toInsert, { onConflict: 'level,subject,starts_at', ignoreDuplicates: true });
  }

  // Propage les paramètres de jeu (durée par question, nb d'étapes, tolérance)
  // aux défis à venir CONSERVÉS — la réconciliation par date ne les touche pas.
  const deleted = new Set(plan.toDelete);
  const survivors = existing.filter((e) => !deleted.has(e.id));
  const paramUpdates = planCampaignParamSync(campaign, slots, survivors);
  for (const u of paramUpdates) {
    await db
      .from('live_challenges')
      .update({
        step_count: u.step_count,
        step_duration_ms: u.step_duration_ms,
        tolerance_ms: u.tolerance_ms,
      })
      .eq('id', u.id);
  }
}

export function useSaveCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CampaignInput): Promise<string> => {
      if (input.id) {
        const { error } = await db
          .from('live_campaigns')
          .update({
            title: input.title,
            subtitle: input.subtitle,
            description: input.description,
            starts_at: input.starts_at,
            ends_at: input.ends_at,
            is_active: input.is_active,
            step_duration_ms: input.step_duration_ms,
            ...(input.special_date !== undefined ? { special_date: input.special_date } : {}),
            ...(input.special_time !== undefined ? { special_time: input.special_time } : {}),
            ...(input.prize_top_n !== undefined ? { prize_top_n: input.prize_top_n } : {}),
          })
          .eq('id', input.id);
        if (error) throw error;
        // Réaligne les défis programmés sur les nouvelles dates / l'état actif.
        await reconcileCampaign(input.id);
        return input.id;
      }

      // Création : slug unique dérivé du titre.
      const base = slugify(input.title) || 'campagne';
      const { data: existing } = await db
        .from('live_campaigns')
        .select('slug')
        .like('slug', `${base}%`);
      const taken = new Set(((existing as { slug: string }[] | null) ?? []).map((r) => r.slug));
      let slug = base;
      for (let i = 2; taken.has(slug); i++) slug = `${base}-${i}`;

      const { data: me } = await db.auth.getUser();
      const { data, error } = await db
        .from('live_campaigns')
        .insert({
          slug,
          title: input.title,
          subtitle: input.subtitle,
          description: input.description,
          starts_at: input.starts_at,
          ends_at: input.ends_at,
          is_active: input.is_active,
          step_duration_ms: input.step_duration_ms,
          kind: input.kind ?? 'slots',
          ...(input.kind === 'special'
            ? {
                special_date: input.special_date ?? null,
                special_time: input.special_time ?? '14:00',
                prize_top_n: input.prize_top_n ?? 10,
              }
            : {}),
          created_by: me?.user?.id ?? null,
        })
        .select('id')
        .single();
      if (error) throw error;
      return (data as { id: string }).id;
    },
    onSettled: () => qc.invalidateQueries({ queryKey: CAMPAIGNS_KEY }),
  });
}

export function useDeleteCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (campaignId: string): Promise<void> => {
      // D'abord annuler les défis programmés (avant que le FK ne les orpheline).
      await cancelFutureScheduled({ campaign_id: campaignId });
      const { error } = await db.from('live_campaigns').delete().eq('id', campaignId);
      if (error) throw error;
    },
    onSettled: () => qc.invalidateQueries({ queryKey: CAMPAIGNS_KEY }),
  });
}

export function useSaveSlot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SlotInput): Promise<void> => {
      const payload = {
        campaign_id: input.campaign_id,
        level: input.level,
        subject: input.subject,
        frequency_count: input.frequency_count,
        frequency_unit: input.frequency_unit,
      };
      if (input.id) {
        const { error } = await db.from('live_campaign_slots').update(payload).eq('id', input.id);
        if (error) throw error;
      } else {
        const { error } = await db.from('live_campaign_slots').insert(payload);
        if (error) throw error;
      }
      // Une cadence/niveau/matière qui change (ou un slot ajouté) doit réaligner
      // immédiatement les défis programmés de la campagne.
      await reconcileCampaign(input.campaign_id);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: CAMPAIGNS_KEY }),
  });
}

export function useDeleteSlot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (slotId: string): Promise<void> => {
      await cancelFutureScheduled({ campaign_slot_id: slotId });
      const { error } = await db.from('live_campaign_slots').delete().eq('id', slotId);
      if (error) throw error;
    },
    onSettled: () => qc.invalidateQueries({ queryKey: CAMPAIGNS_KEY }),
  });
}

/**
 * Applique une HEURE COMMUNE à tous les défis programmés (à venir) d'une campagne :
 *  1. recale l'ancre `live_campaigns.starts_at` (date conservée, heure = la nouvelle) ;
 *  2. décale chaque défi `scheduled` à venir EN PLACE (même date, heure = la nouvelle)
 *     → l'id est conservé, donc l'exercice épinglé ET le payload restent intacts ;
 *  3. réconcilie pour combler d'éventuels trous (heures déjà alignées → pas de churn).
 *
 * Renvoie le nombre de défis décalés.
 */
export function useApplyCampaignTime() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      campaignId: string;
      hour: number;
      minute: number;
    }): Promise<number> => {
      const { campaignId, hour, minute } = input;

      // 1. Recale l'ancre de la campagne (sinon les futures occurrences seedées
      //    repartiraient sur l'ancienne heure).
      const { data: campaignData } = await db
        .from('live_campaigns')
        .select('starts_at')
        .eq('id', campaignId)
        .maybeSingle();
      const campaign = campaignData as { starts_at: string } | null;
      if (campaign) {
        const { error } = await db
          .from('live_campaigns')
          .update({ starts_at: applyLocalTimeOfDay(campaign.starts_at, hour, minute) })
          .eq('id', campaignId);
        if (error) throw error;
      }

      // 2. Décale en place les défis programmés à venir.
      const { data: existingData } = await db
        .from('live_challenges')
        .select('id, starts_at')
        .eq('campaign_id', campaignId)
        .eq('status', 'scheduled')
        .gt('starts_at', new Date().toISOString());
      const updates = planCampaignTimeShift(
        (existingData as TimeShiftChallenge[] | null) ?? [],
        hour,
        minute,
      );
      for (const u of updates) {
        const { error } = await db
          .from('live_challenges')
          .update({ starts_at: u.starts_at, week_start: u.week_start })
          .eq('id', u.id);
        if (error) throw error;
      }

      // 3. Comble les trous éventuels (occurrences non encore matérialisées).
      await reconcileCampaign(campaignId);
      return updates.length;
    },
    onSettled: () => qc.invalidateQueries({ queryKey: CAMPAIGNS_KEY }),
  });
}

// ── Conversions datetime-local ⇄ ISO ─────────────────────────────────────────

/** ISO UTC → valeur `<input type="datetime-local">` (heure LOCALE du navigateur). */
export function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Valeur datetime-local (heure locale) → ISO UTC. '' → null. */
export function localInputToIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * Cover d'une campagne choisie parmi les images prédéfinies des lives
 * (migration 0658) : on enregistre son URL publique comme illustration.
 */
export function useSetCampaignCover() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { campaignId: string; url: string }) => {
      const { error } = await db
        .from('live_campaigns')
        .update({ illustration_url: input.url })
        .eq('id', input.campaignId);
      if (error) throw error;
    },
    onSettled: () => qc.invalidateQueries({ queryKey: CAMPAIGNS_KEY }),
  });
}
